import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { trackPageView } from "@/lib/analytics";

export const SITE_URL = "https://suportededomingo.com.br";
export const SITE_NAME = "Suporte de Domingo";
export const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

const OG_LOCALE: Record<string, string> = {
  pt: "pt_BR",
  en: "en_US",
  es: "es_ES",
};

type Seo = {
  title: string;
  description: string;
  path: string;
  image?: string;
  type?: "website" | "article";
  noindex?: boolean;
};

function upsert(
  selector: string,
  create: () => HTMLElement,
  apply: (el: HTMLElement) => void,
) {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  apply(el);
}

function meta(name: string, content: string) {
  upsert(
    `meta[name="${name}"]`,
    () => {
      const el = document.createElement("meta");
      el.setAttribute("name", name);
      return el;
    },
    (el) => el.setAttribute("content", content),
  );
}

function property(prop: string, content: string) {
  upsert(
    `meta[property="${prop}"]`,
    () => {
      const el = document.createElement("meta");
      el.setAttribute("property", prop);
      return el;
    },
    (el) => el.setAttribute("content", content),
  );
}

function link(rel: string, href: string) {
  upsert(
    `link[rel="${rel}"]`,
    () => {
      const el = document.createElement("link");
      el.setAttribute("rel", rel);
      return el;
    },
    (el) => el.setAttribute("href", href),
  );
}

export function useSeo({
  title,
  description,
  path,
  image = DEFAULT_IMAGE,
  type = "website",
  noindex = false,
}: Seo) {
  const { i18n } = useTranslation();
  const language = i18n.language;

  useEffect(() => {
    const url = `${SITE_URL}${path}`;
    const fullTitle =
      title === SITE_NAME ? title : `${title} — ${SITE_NAME}`;

    document.title = fullTitle;

    meta("description", description);
    meta("robots", noindex ? "noindex, follow" : "index, follow");
    link("canonical", url);

    property("og:site_name", SITE_NAME);
    property("og:title", fullTitle);
    property("og:description", description);
    property("og:url", url);
    property("og:type", type);
    property("og:image", image);
    property("og:image:alt", fullTitle);
    property("og:locale", OG_LOCALE[language] ?? "pt_BR");

    meta("twitter:card", "summary_large_image");
    meta("twitter:title", fullTitle);
    meta("twitter:description", description);
    meta("twitter:image", image);

    trackPageView(fullTitle, language);
  }, [title, description, path, image, type, noindex, language]);
}
